import axios from 'axios';

const API_BASE = '/api';

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

// Auth
export const exchangeSession = (sessionId) => api.post('/auth/session', { session_id: sessionId });
export const registerUser = (data) => api.post('/auth/register', data);
export const loginUser = (data) => api.post('/auth/login', data);
export const getMe = () => api.get('/auth/me');
export const logout = () => api.post('/auth/logout');
export const forgotPassword = (email) => api.post('/auth/forgot-password', { email });
export const resetPassword = (token, password) => api.post('/auth/reset-password', { token, password });

// Students
export const getStudents = (classe) => api.get('/students', { params: classe ? { classe, limit: 5000 } : { limit: 5000 } });
export const createStudent = (data) => api.post('/students', data);
export const deleteStudent = (id) => api.delete(`/students/${id}`);
export const importStudentsCSV = (file, classe) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('classe', classe);
  return api.post('/students/import-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const updateStudentCode = (studentId, code) => api.put(`/students/${studentId}/code`, { student_code: code });
export const exportStudentsCSV = (classe) => api.get('/students/export-csv', { params: classe ? { classe } : {}, responseType: 'blob' });
export const exportStudentsPdfIds = (classe) => api.get('/students/pdf-ids', { params: { classe }, responseType: 'blob' });
export const getClasses = () => api.get('/classes');

// Teacher Classes (custom classes)
export const getTeacherClasses = () => api.get('/teacher-classes');
export const createTeacherClass = (data) => api.post('/teacher-classes', data);
export const updateTeacherClass = (id, data) => api.put(`/teacher-classes/${id}`, data);
export const deleteTeacherClass = (id) => api.delete(`/teacher-classes/${id}`);

// Quizzes
export const getQuizzes = () => api.get('/quizzes', { params: { limit: 500 } });
export const getQuiz = (id) => api.get(`/quizzes/${id}`);
export const createQuiz = (data) => api.post('/quizzes', data);
export const updateQuiz = (id, data) => api.put(`/quizzes/${id}`, data);
export const deleteQuiz = (id) => api.delete(`/quizzes/${id}`);
export const getCascadeInfo = (id) => api.get(`/quizzes/${id}/cascade-info`);
export const duplicateQuiz = (id) => api.post(`/quizzes/${id}/duplicate`);

// PDF Generation
export const generatePDFs = (quizId) => api.post(`/quizzes/${quizId}/generate-pdfs`);
export const downloadZip = (quizId) => api.get(`/quizzes/${quizId}/download-zip`, { responseType: 'blob' });
export const downloadQCMs = (quizId) => api.get(`/quizzes/${quizId}/download-qcms`, { responseType: 'blob' });
export const previewQCMsPdf = (quizId) => api.get(`/quizzes/${quizId}/preview-pdf`, { responseType: 'blob' });

// Direct browser download via axios (with auth interceptor)
export const triggerDownloadQCMs = async (quizId) => {
  try {
    const resp = await api.get(`/quizzes/${quizId}/download-qcms`, { responseType: 'blob' });
    const blob = resp.data;
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `qcm_${quizId}.pdf`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(blobUrl); }, 200);
  } catch (e) {
    // Axios blob errors: response.data is a Blob, need to read it as text
    let msg = 'Erreur de téléchargement';
    if (e.response?.data instanceof Blob) {
      try {
        const text = await e.response.data.text();
        const parsed = JSON.parse(text);
        msg = parsed.detail || msg;
      } catch (parseErr) { console.warn('Blob parse error:', parseErr); }
    } else if (e.response?.data?.detail) {
      msg = e.response.data.detail;
    }
    throw new Error(msg);
  }
};

// Scanner - quiz_id is optional, the exam ID in the paper identifies everything
export const scanSheet = (data) => api.post('/scan', data);

// Async scan jobs (chunked upload + background OCR with progress)
export const SCAN_CHUNK_SIZE = 4 * 1024 * 1024;
export const uploadScanChunk = (uploadId, index, totalChunks, blob) => {
  const fd = new FormData();
  fd.append('upload_id', uploadId); fd.append('index', index); fd.append('total_chunks', totalChunks);
  fd.append('file', blob, `chunk_${index}`);
  return api.post('/scan-jobs/upload-chunk', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
};
export const startScanJob = (uploads) => api.post('/scan-jobs/start', { uploads });
export const getScanJob = (jobId) => api.get(`/scan-jobs/${jobId}`);
export const getActiveScanJob = () => api.get('/scan-jobs/active');
export const cancelScanJob = (jobId) => api.post(`/scan-jobs/${jobId}/cancel`);

// Results
export const getResults = (quizId) => api.get('/results', { params: { ...(quizId ? { quiz_id: quizId } : {}), limit: 500 } });
export const getResultDetail = (resultId) => api.get(`/results/detail/${resultId}`);
export const deleteResult = (resultId) => api.delete(`/results/${resultId}`);
export const getQuizStats = (quizId) => api.get(`/results/stats/${quizId}`);
export const exportCSV = (quizId) => api.get(`/results/export-csv/${quizId}`, { responseType: 'blob' });
export const exportXLSX = (quizId) => api.get(`/results/export-xlsx/${quizId}`, { responseType: 'blob' });

// AI
export const generateQuestions = (data) => api.post('/ai/generate-questions', data);
export const estimateTime = (data) => api.post('/ai/estimate-time', data);

// Documents
export const uploadDocument = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteDocument = (docId) => api.delete(`/documents/${docId}`);
export const listDocuments = () => api.get('/documents');

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// Admin
export const getAdminStats = () => api.get('/admin/stats');
export const getAdminUsers = () => api.get('/admin/users');
export const toggleUserStatus = (userId) => api.post(`/admin/users/${userId}/toggle-status`);
export const deleteUser = (userId) => api.delete(`/admin/users/${userId}`);
export const getAdminContacts = () => api.get('/admin/contact-requests');
export const updateContactRequest = (requestId, status) => api.post(`/admin/contact-requests/${requestId}/update`, { status });
export const deleteContactRequest = (requestId) => api.delete(`/admin/contact-requests/${requestId}`);
export const getAdminContactMessages = () => api.get('/admin/contact-messages');
export const updateContactMessageStatus = (requestId, status) => api.post(`/admin/contact-messages/${requestId}/status`, { status });
export const deleteContactMessage = (requestId) => api.delete(`/admin/contact-messages/${requestId}`);
export const getAdminUserDetails = (userId) => api.get(`/admin/users/${userId}/details`);
export const cleanupOrphans = () => api.post('/admin/cleanup-orphans');

// Contact
export const sendChatMessage = (message, sessionId) => api.post('/chat', { message, session_id: sessionId });

export const submitContact = (data) => api.post('/contact', data);

export const submitContactMessage = (data) => api.post('/contact-message', data);

// Analytics - Tracking
export const getTagsTree = () => api.get('/tags/tree');
export const createTag = (data) => api.post('/tags', data);
export const updateTag = (tagId, data) => api.put(`/tags/${tagId}`, data);
export const deleteTag = (tagId) => api.delete(`/tags/${tagId}`);
export const importProgram = (data) => api.post('/tags/import-program', data);
export const uploadProgram = (formData) => api.post('/tags/upload-program', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
export const suggestNotion = (questionText, options) => api.post('/tags/suggest-notion', { question_text: questionText, options: options || [] });
export const getClassAnalyticsSummary = (classId, params) => api.get(`/classes/${classId}/analytics/summary`, { params });
export const getClassAnalyticsStudents = (classId, params) => api.get(`/classes/${classId}/analytics/students`, { params });
export const getClassAnalyticsMastery = (classId, params) => api.get(`/classes/${classId}/analytics/mastery`, { params });
export const getClassAnalyticsInsights = (classId, params) => api.get(`/classes/${classId}/analytics/insights`, { params });
export const getStudentAnalyticsSummary = (studentId, params) => api.get(`/students/${studentId}/analytics/summary`, { params });
export const getStudentAnalyticsMastery = (studentId, params) => api.get(`/students/${studentId}/analytics/mastery`, { params });
export const getStudentAnalyticsHistory = (studentId, params) => api.get(`/students/${studentId}/analytics/history`, { params });
export const getStudentAnalyticsDifficulties = (studentId, params) => api.get(`/students/${studentId}/analytics/recurrent-difficulties`, { params });

// Document formula verification
export const getDocumentFormulas = (docId) => api.get(`/documents/${docId}/formulas`);
export const getFormulaCropUrl = (cropId) => `${process.env.REACT_APP_BACKEND_URL}/api/documents/crops/${cropId}`;

export default api;
